import React, { useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useI18n, SUPPORTED_LOCALES } from "@/contexts/I18nContext";
import { Menu, X, LineChart } from "lucide-react";

function switchLocalePath(pathname, targetLocale) {
  const parts = pathname.split("/").filter(Boolean);
  if (parts.length === 0) return `/${targetLocale}`;
  if (SUPPORTED_LOCALES.includes(parts[0])) {
    parts[0] = targetLocale;
    return "/" + parts.join("/");
  }
  return "/" + targetLocale + pathname;
}

export default function Header() {
  const { t, locale } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const base = `/${locale}`;
  const navItems = [
    { key: "home", href: `${base}` },
    { key: "insights", href: `${base}#why` },
    { key: "projects", href: `${base}#projects` },
    { key: "transparency", href: `${base}#transparency` },
  ];

  const handleLocaleSwitch = (target) => {
    const nextPath = switchLocalePath(location.pathname, target);
    navigate(nextPath + location.hash, { replace: false });
    setOpen(false);
  };

  const handleNav = (href) => {
    setOpen(false);
    if (href.includes("#")) {
      const [path, hash] = href.split("#");
      if (location.pathname !== path) {
        navigate(path);
        setTimeout(() => {
          const el = document.getElementById(hash);
          if (el) el.scrollIntoView({ block: "start" });
        }, 50);
      } else {
        const el = document.getElementById(hash);
        if (el) el.scrollIntoView({ block: "start" });
      }
    } else {
      navigate(href);
    }
  };

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 w-full bg-[#0A192F] border-b border-white/10"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
        <div className="flex h-20 items-center justify-between">
          <Link
            data-testid="header-brand-link"
            to={base}
            className="flex items-center gap-3 text-white"
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-sm bg-[#059669]">
              <LineChart className="h-5 w-5 text-white" strokeWidth={2} />
            </span>
            <span className="flex flex-col leading-tight">
              <span className="font-heading text-base font-semibold tracking-tight text-white">
                {t("brand.name")}
              </span>
              <span className="text-[11px] uppercase tracking-widest text-[#94A3B8]">
                {t("brand.tagline")}
              </span>
            </span>
          </Link>

          {/* Desktop nav */}
          <nav
            className="hidden lg:flex items-center gap-8"
            aria-label="Primary"
          >
            {navItems.map((item) => (
              <button
                key={item.key}
                data-testid={`nav-${item.key}`}
                onClick={() => handleNav(item.href)}
                className="text-sm font-medium text-[#CBD5E1] hover:text-white"
              >
                {t(`nav.${item.key}`)}
              </button>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-4">
            <div
              className="flex items-center gap-1 rounded-sm border border-white/15 px-1 py-1"
              role="group"
              aria-label={t("nav.language")}
            >
              {SUPPORTED_LOCALES.map((code) => (
                <button
                  key={code}
                  data-testid={`lang-switch-${code}`}
                  onClick={() => handleLocaleSwitch(code)}
                  className={
                    "px-2.5 py-1 text-xs font-semibold uppercase tracking-wider " +
                    (locale === code
                      ? "bg-[#059669] text-white"
                      : "text-[#CBD5E1] hover:text-white")
                  }
                >
                  {code}
                </button>
              ))}
            </div>
            <button
              data-testid="header-cta"
              onClick={() => handleNav(`${base}#newsletter`)}
              className="rounded-sm bg-[#059669] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#047857]"
            >
              {t("nav.cta")}
            </button>
          </div>

          {/* Mobile hamburger */}
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-white p-2"
            aria-label={open ? t("nav.closeMenu") : t("nav.openMenu")}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu drawer */}
      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden border-t border-white/10 bg-[#0A192F]"
        >
          <div className="mx-auto max-w-7xl px-4 py-6 flex flex-col gap-4">
            {navItems.map((item) => (
              <button
                key={item.key}
                data-testid={`mobile-nav-${item.key}`}
                onClick={() => handleNav(item.href)}
                className="text-left text-base font-medium text-white py-2 border-b border-white/10"
              >
                {t(`nav.${item.key}`)}
              </button>
            ))}
            <div className="flex items-center gap-2 pt-2">
              <span className="text-xs uppercase tracking-widest text-[#94A3B8]">
                {t("nav.language")}
              </span>
              {SUPPORTED_LOCALES.map((code) => (
                <button
                  key={code}
                  data-testid={`mobile-lang-switch-${code}`}
                  onClick={() => handleLocaleSwitch(code)}
                  className={
                    "px-3 py-1 text-xs font-semibold uppercase tracking-wider rounded-sm " +
                    (locale === code
                      ? "bg-[#059669] text-white"
                      : "border border-white/15 text-[#CBD5E1]")
                  }
                >
                  {code}
                </button>
              ))}
            </div>
            <button
              data-testid="mobile-header-cta"
              onClick={() => handleNav(`${base}#newsletter`)}
              className="mt-2 rounded-sm bg-[#059669] px-5 py-3 text-sm font-semibold text-white"
            >
              {t("nav.cta")}
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
